import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export interface CookieCategoryPreferences {
  /** Strictly necessary cookies — always true, cannot be disabled. */
  essential: boolean;
  /** Performance / analytics cookies (e.g. Google Analytics). */
  performance: boolean;
  /** Functional cookies (preferences, language, theme persistence beyond required). */
  functional: boolean;
  /** Marketing / third-party advertising and tracking cookies. */
  marketing: boolean;
}

export interface CookieConsentRecord {
  preferences: CookieCategoryPreferences;
  /** ISO timestamp of when the user gave / updated consent. */
  consentedAt: string;
  /** Schema version, lets us re-prompt if categories change. */
  version: number;
}

@Injectable({
  providedIn: "root",
})
export class CookieConsentService {
  /** localStorage key for the persisted consent record. */
  private readonly storageKey = "flowchest_cookie_consent";

  /**
   * Bump this when the cookie categories or wording change in a way that
   * requires re-collecting consent from existing users.
   */
  private readonly currentVersion = 1;

  private readonly defaultPreferences: CookieCategoryPreferences = {
    essential: true,
    performance: false,
    functional: false,
    marketing: false,
  };

  private readonly visible$ = new BehaviorSubject<boolean>(false);
  private readonly preferences$ = new BehaviorSubject<CookieCategoryPreferences>(
    this.defaultPreferences
  );

  constructor() {
    const stored = this.readStoredRecord();
    if (stored) {
      this.preferences$.next(stored.preferences);
    }
  }

  /** Stream consumers can subscribe to in order to know when to render the banner. */
  get visibility$(): Observable<boolean> {
    return this.visible$.asObservable();
  }

  /** Stream of the currently-applied cookie preferences. */
  get currentPreferences$(): Observable<CookieCategoryPreferences> {
    return this.preferences$.asObservable();
  }

  get currentPreferences(): CookieCategoryPreferences {
    return this.preferences$.value;
  }

  /** Returns true if the user has already saved a valid consent record. */
  hasConsented(): boolean {
    return this.readStoredRecord() !== null;
  }

  /**
   * Show the banner on first load when no consent record exists.
   * Safe to call multiple times — it is a no-op once consent is saved.
   */
  initialize(): void {
    if (!this.hasConsented()) {
      this.visible$.next(true);
    }
  }

  /** Open the consent banner manually (e.g. from a "Manage cookies" link). */
  openSettings(): void {
    this.visible$.next(true);
  }

  /** Hide the banner without changing the saved preferences. */
  close(): void {
    this.visible$.next(false);
  }

  /** Accept every category. */
  acceptAll(): void {
    this.persist({
      essential: true,
      performance: true,
      functional: true,
      marketing: true,
    });
  }

  /** Accept only the strictly-necessary category. */
  acceptMandatoryOnly(): void {
    this.persist({ ...this.defaultPreferences });
  }

  /** Persist a custom selection coming from the "Customize" view. */
  savePreferences(preferences: Partial<CookieCategoryPreferences>): void {
    this.persist({
      ...this.defaultPreferences,
      ...preferences,
      essential: true,
    });
  }

  private persist(preferences: CookieCategoryPreferences): void {
    const record: CookieConsentRecord = {
      preferences,
      consentedAt: new Date().toISOString(),
      version: this.currentVersion,
    };

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(record));
    } catch {
      // localStorage may be unavailable (private mode, quota); ignore so the
      // UI still updates for the current session.
    }

    this.preferences$.next(preferences);
    this.visible$.next(false);
  }

  private readStoredRecord(): CookieConsentRecord | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as CookieConsentRecord;
      if (!parsed || parsed.version !== this.currentVersion || !parsed.preferences) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}
